import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import styled from 'styled-components';
import { IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import TitledContainer from '../../components/TitledContainer';
import Typography from '../../components/Typography';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { CampaignBase, CampaignReduced, CampaignStatus } from '../../models';
import {
    archiveCampaignAsync,
    createCampaignAsync,
    deleteCampaignAsync,
    duplicateCampaignAsync,
    getCampaignsAsync,
} from '../../ajax/requests';
import { showError } from '../../store/entities/actions';
import { alertSuccess } from '../../store/alerts/actions';
import { CampaignsToolbar } from './CampaignsToolbar';
import { CampaignsTable } from './CampaignsTable';
import { CampaignDeleteOrArchiveModal } from './CampaignDeleteOrArchiveModal';
import { buildNewCampaignBase, normalizeCampaignSearch } from './campaignsUtils';
import { paths } from '../../app/constants';
import WhatsAppTemplatesSettings from '../Settings/WhatsAppTemplatesSettings';

const PageBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-M);
`;

const LoadingState = styled.div`
    display: flex;
    justify-content: center;
    padding: var(--spacer-L);
`;

const AddCampaignButton = styled(IconButton)`
    && {
        padding: var(--spacer-XS);
        color: var(--text-muted);
        border: var(--border-thin) dashed var(--appLigthGrey);
        border-radius: var(--spacer-XS);
        width: 100%;
        transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

        &:hover {
            border-color: var(--success-color);
            color: var(--success-color);
            background: var(--success-bg);
        }
    }
`;

type CampaignMutation = {
    campaign: CampaignReduced;
    action: 'delete' | 'archive';
};

export const CampaignsPage: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [campaigns, setCampaigns] = useState<CampaignReduced[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [channelValue, setChannelValue] = useState('');
    const [pendingMutation, setPendingMutation] = useState<CampaignMutation | undefined>();
    const [mutationLoading, setMutationLoading] = useState(false);
    const [showTemplatesSettings, setShowTemplatesSettings] = useState(false);

    const loadCampaigns = useCallback(() => {
        setLoading(true);
        getCampaignsAsync()
            .then(data => setCampaigns([...data].sort((a, b) => b.lastUpdateDt.localeCompare(a.lastUpdateDt))))
            .catch(error => {
                console.error(error);
                dispatch(showError(error));
            })
            .finally(() => setLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    const filteredCampaigns = useMemo(() => {
        const searchTerm = normalizeCampaignSearch(searchValue);

        return campaigns.filter(campaign => {
            const matchesSearch = !searchTerm || normalizeCampaignSearch(campaign.name).includes(searchTerm);
            const matchesStatus = !statusValue || String(campaign.status) === statusValue;
            const matchesChannel = !channelValue || String(campaign.channel) === channelValue;

            return matchesSearch && matchesStatus && matchesChannel;
        });
    }, [campaigns, channelValue, searchValue, statusValue]);

    const openEditor = useCallback((campaignId: string) => {
        history.push(paths.campaign(campaignId));
    }, [history]);

    const handleCreateCampaign = useCallback(async () => {
        setCreating(true);

        try {
            const content = await fetch('/default-campaign-content.html').then(response => response.text());
            const data: CampaignBase = buildNewCampaignBase({
                name: t(TK.newCampaign),
                subject: t(TK.newCampaign),
                content,
            });

            const id = await createCampaignAsync(data);
            history.push(`${paths.campaign(id)}?new=1`);
            dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setCreating(false);
        }
    }, [dispatch, history, t]);

    const handleDuplicateCampaign = useCallback(async (campaign: CampaignReduced) => {
        try {
            const newId = await duplicateCampaignAsync(campaign.id);
            dispatch(alertSuccess(t(TK.entityCreatedSuccessfully)));
            history.push(paths.campaign(newId));
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        }
    }, [dispatch, history, t]);

    const handleAnalyzeCampaign = useCallback((campaign: CampaignReduced) => {
        history.push(paths.campaignAnalytics(campaign.id));
    }, [history]);

    const handleArchiveOrDelete = useCallback((campaign: CampaignReduced) => {
        const action = campaign.status === CampaignStatus.Draft || campaign.status === CampaignStatus.Ready ? 'delete' : 'archive';
        setPendingMutation({ campaign, action });
    }, []);

    const closeMutationModal = useCallback(() => {
        if (mutationLoading) return;
        setPendingMutation(undefined);
    }, [mutationLoading]);

    const confirmMutation = useCallback(async () => {
        if (!pendingMutation) return;

        setMutationLoading(true);
        try {
            if (pendingMutation.action === 'delete') {
                await deleteCampaignAsync(pendingMutation.campaign.id);
                dispatch(alertSuccess(t(TK.entityDeletedSuccessfully)));
            } else {
                await archiveCampaignAsync(pendingMutation.campaign.id);
                dispatch(alertSuccess(t(TK.entityChangedSuccessfully)));
            }
            setPendingMutation(undefined);
            loadCampaigns();
        } catch (error) {
            console.error(error);
            dispatch(showError(error));
        } finally {
            setMutationLoading(false);
        }
    }, [dispatch, loadCampaigns, pendingMutation, t]);

    const hasFilters = normalizeCampaignSearch(searchValue).length > 0 || statusValue.length > 0 || channelValue.length > 0;

    return (
        <TitledContainer id="campaigns" title={t(TK.campaigns)}>
            <PageBody>
                <CampaignsToolbar
                    searchValue={searchValue}
                    statusValue={statusValue}
                    channelValue={channelValue}
                    onSearchChange={setSearchValue}
                    onStatusChange={setStatusValue}
                    onChannelChange={setChannelValue}
                    onCreate={handleCreateCampaign}
                    creating={creating}
                    onSettingsClick={() => setShowTemplatesSettings(true)}
                />

                {loading && !campaigns.length ? (
                    <LoadingState>
                        <Spinner />
                    </LoadingState>
                ) : hasFilters && !filteredCampaigns.length ? null : (
                    <CampaignsTable
                        campaigns={filteredCampaigns}
                        loading={loading}
                        hasFilters={hasFilters}
                        onEdit={campaign => openEditor(campaign.id)}
                        onAnalyze={handleAnalyzeCampaign}
                        onDuplicate={handleDuplicateCampaign}
                        onArchiveOrDelete={handleArchiveOrDelete}
                    />
                )}

                {!loading && !filteredCampaigns.length && hasFilters ? (
                    <Typography>{t(TK.campaignsNoResults)}</Typography>
                ) : null}

                {!loading && (
                    <Tooltip title={t(TK.newCampaign)} placement="top">
                        <AddCampaignButton onClick={handleCreateCampaign} disabled={creating}>
                            <AddIcon />
                        </AddCampaignButton>
                    </Tooltip>
                )}
            </PageBody>

            <CampaignDeleteOrArchiveModal
                campaign={pendingMutation?.campaign}
                action={pendingMutation?.action ?? 'delete'}
                isOpen={!!pendingMutation}
                loading={mutationLoading}
                onConfirm={confirmMutation}
                onClose={closeMutationModal}
            />

            <Modal isOpen={showTemplatesSettings} toggle={() => setShowTemplatesSettings(false)} size="lg">
                <ModalHeader toggle={() => setShowTemplatesSettings(false)}>
                    {t(TK.whatsappTemplates)}
                </ModalHeader>
                <ModalBody>
                    <WhatsAppTemplatesSettings id="campaigns-whatsapp-templates" values={null} onChange={() => {}} />
                </ModalBody>
            </Modal>
        </TitledContainer>
    );
}
